import React, { useEffect, useMemo, useState } from 'react';

import { PosOption, ProductVariant, type MenuOption } from 'generated/graphql';

import { formatTextToPriceInt } from 'utils';
import { cloneDeep } from 'lodash';

import TitleSwitch from 'components/Inputs/TitleSwitch';
import Image from 'components/Image/Image';
import useScreenWidthQuery from 'common/Hooks/UseScreenWidthQuery';
import { WindowBreakpoints } from 'utils/DocumentHelper';
import { useItemContext } from 'components/SingleItem/ItemContext';
import OptionList, { PricesRow, Row } from './OptionList';

import styles from './OptionGroups.module.scss';
import detailsStyles from '../Details.module.scss';

interface Props {
  optionGroup: MenuOption;
  handleChange: (option: MenuOption) => void;
  isSameOnAllApps: boolean;
  posOption: PosOption | null | undefined;
  variants: ProductVariant[];
  isDragDisabled: boolean;
  toggleDragOnEdits: (type: 'focus' | 'enter' | 'blur') => void;
  isRequiredOption?: boolean;
}

const OptionGroupItem: React.FC<Props> = ({
  optionGroup,
  handleChange,
  isSameOnAllApps,
  posOption,
  variants,
  isDragDisabled,
  toggleDragOnEdits,
  isRequiredOption
}) => {
  const [innerWidth] = useScreenWidthQuery();
  const [isMobileView, setIsMobileView] = useState(innerWidth < WindowBreakpoints.SM);
  const { currentVenue, item } = useItemContext();

  useEffect(() => {
    const isMobileViewNew = innerWidth < WindowBreakpoints.SM;
    if (isMobileViewNew !== isMobileView) {
      setIsMobileView(isMobileViewNew);
    }
  }, [innerWidth, isMobileView]);

  const options: Row[] = useMemo((): Row[] => {
    if (!variants) {
      return [];
    }

    const tempPosRow: PricesRow = {
      id: '-1',
      image: '/Images/YourPos.png',
      imageAlt: `Icon for Your POS`,
      prices: [2300],
      disabled: true
    };

    return variants
      .map((variant) => {
        const isEnabled = optionGroup.variants.find((_variant) => _variant.posId === variant.posId);

        const variantInPosData = posOption?.variants.find((_variant) => _variant.posId === variant.posId);

        const posPrice = variantInPosData ? variantInPosData.price : null;

        const tempPosRowWithPrice = {
          ...tempPosRow,
          prices: [posPrice]
        };

        const prices = [];

        if (!isMobileView) {
          prices.push(tempPosRowWithPrice);
        }

        if (isSameOnAllApps) {
          const combinedPrices = variant.variablesByApp.map(({ price }) => price || 0).sort();

          prices.push({
            id: '-2',
            prices: combinedPrices,
            disabled: false
          });
        } else {
          variant.variablesByApp.forEach(({ app, price }) => {
            prices.push({
              id: app.id,
              image: app.image,
              imageAlt: `Icon for ${app.name}`,
              prices: [parseInt(price ? price.toString() : '0', 10)]
            });
          });
        }

        const itemHasSurcharge = item?.variablesByApp.find((app) => app.surcharge && app.surcharge.amount > 0);
        const surcharge = itemHasSurcharge
          ? variant.variablesByApp.find((app) => app.surcharge && app.surcharge.amount > 0)?.surcharge
          : null;

        return {
          label: variant.name,
          prices,
          availability: variant.variablesByApp.some(
            (app) => app.availability !== variant.variablesByApp[0].availability
          )
            ? 'mixed'
            : variant.variablesByApp[0].availability || 'unknown',
          id: variant.posId,
          disabled: !isEnabled,
          surcharge: surcharge || null, // we just need 1 surcharge item as it does not matter the percentage for options
          _typename: variant.__typename || 'Variant'
        };
      })
      .sort((a, b) => {
        const aVariantIndex = optionGroup.variants.findIndex((variant) => variant.posId === a.id);
        const bVariantIndex = optionGroup.variants.findIndex((variant) => variant.posId === b.id);

        return aVariantIndex - bVariantIndex;
      })
      .sort((a, b) => {
        if (a.disabled === b.disabled) {
          return 0;
        }

        if (a.disabled) {
          return 1;
        }

        return -1;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants, isSameOnAllApps, posOption?.variants, optionGroup.variants, isMobileView]);

  const headings = useMemo(() => {
    if (!variants) {
      return [];
    }
    const response: { key: string; value: string | JSX.Element }[] = [
      {
        key: '',
        value: ''
      }
    ];

    if (!isMobileView) {
      response.push({
        key: 'pos',
        value: <Image key="pos" fallbackUrl="/Images/NoApp.png" src="/Images/YourPos.png" alt="Icon for Your POS" />
      });
    }

    if (isSameOnAllApps) {
      response.push({
        key: '-1',
        value: 'Apps'
      });
    } else {
      response.push(
        ...(variants.length
          ? variants[0].variablesByApp.map(({ app }) => {
              const { name, image, id } = app;
              const src = image || '/Images/NoApp.png';

              return {
                key: id.toString(),
                value: <Image key={name} fallbackUrl="/Images/NoApp.png" src={src} alt={`Icon for ${name}`} />
              };
            })
          : [])
      );
    }

    return response;
  }, [variants, isSameOnAllApps, isMobileView]);

  if (!optionGroup) {
    return null;
  }

  const { posId, variants: enabledVariants, max, min } = optionGroup;

  const optionParagraph: string = (() => {
    if (min && max && min !== max) {
      return `Customers are required to select between ${min} and ${max} options.`;
    }
    if (min && max && min === max) {
      const optionText = `option${min > 1 ? 's' : ''}`;
      return `Customers are required to select at least ${min} ${optionText}.`;
    }
    if (max) {
      const optionText = `option${max > 1 ? 's' : ''}`;
      return `Customers can select a maximum of ${max} ${optionText}.`;
    }
    if (min) {
      const optionText = `option${min > 1 ? 's' : ''}`;
      return `Customers are required to select at least ${min} ${optionText}.`;
    }
    return '';
  })();

  const setMax = (newMax: number): void => {
    handleChange({
      ...optionGroup,
      max: newMax
    });
  };

  const setMin = (newMin: number): void => {
    handleChange({
      ...optionGroup,
      min: newMin
    });
  };

  const handlePriceChange = (index: number, id: string, value: string): void => {
    const response = formatTextToPriceInt(value);

    const variant = options[index];
    const variantIndexInEnabledVariants = enabledVariants.findIndex(
      (enabledVariant) => enabledVariant.posId === variant.id
    );

    const newVariants = [...enabledVariants];
    const clonedRow = cloneDeep(newVariants[variantIndexInEnabledVariants]);

    if (isSameOnAllApps) {
      clonedRow.variablesByApp.forEach((appPrice) => (appPrice.price = response));
    } else {
      const priceByAppInstance = clonedRow.variablesByApp.find((appPrice) => appPrice.app.id === id)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

      priceByAppInstance.price = response;
    }

    newVariants[variantIndexInEnabledVariants] = clonedRow;

    handleChange({
      ...optionGroup,
      variants: newVariants
    });
  };

  const handleNameChange = (index: number, value: string): void => {
    const variant = options[index];
    const variantIndexInEnabledVariants = enabledVariants.findIndex(
      (enabledVariant) => enabledVariant.posId === variant.id
    );

    const newVariants = [...enabledVariants];
    const clonedRow = cloneDeep(newVariants[variantIndexInEnabledVariants]);

    clonedRow.name = value;

    newVariants[variantIndexInEnabledVariants] = clonedRow;

    handleChange({
      ...optionGroup,
      variants: newVariants
    });
  };

  const handleToggle = (index: number, isChecked?: boolean): void => {
    const row = options[index];
    const option = variants.find((variant) => variant.posId === row.id);
    if (!option) {
      return;
    }
    const newVariants = [...enabledVariants];

    const variantIndex = newVariants.findIndex((variant) => variant.posId === option?.posId);

    const surchargeApps = option.variablesByApp.filter((app) => app.surcharge && app.surcharge.amount > 0);
    let newOption: ProductVariant | undefined;
    if (isChecked && surchargeApps.length) {
      surchargeApps.forEach((app) => {
        if (app.surcharge && app.surcharge.amount > 0) {
          const newPrice = Number(Math.round((app.price ?? 0) * (1 + app.surcharge.amount / 100)).toFixed(2));
          if (!newOption) {
            newOption = cloneDeep(option);
          }
          const newOptionApp = newOption.variablesByApp.find((appPrice) => appPrice.app.id === app.app.id);
          if (newOptionApp) {
            newOptionApp.price = newPrice;
          }
        }
      });
    }

    if (isRequiredOption && enabledVariants.length === 1 && variantIndex !== -1) {
      return;
    }

    if (variantIndex === -1) {
      newVariants.push(newOption || option);
    } else {
      newVariants.splice(variantIndex, 1);
    }

    handleChange({
      ...optionGroup,
      variants: newVariants
    });
  };

  return (
    <div className={styles.OptionGroup} data-testid="optionGroup-item">
      {!isRequiredOption && (
        <>
          <div className={styles.TopOptions}>
            <TitleSwitch
              title="Customers are required to select an option"
              checked={!!optionGroup.min}
              onChange={(hasMin: boolean) => {
                handleChange({
                  ...optionGroup,
                  min: hasMin ? 1 : 0
                });
              }}
            />
          </div>
          {min > 0 ? (
            <div className={styles.TopOptions}>
              <p>Minimum number of options customers can select</p>
              <div className={styles.Incrementer}>
                <button
                  aria-label="Decrease minimum number of options"
                  disabled={min === 0}
                  onClick={() => setMin(min - 1)}
                  type="button"
                  data-testid="min-minus"
                >
                  <i className="mm-minus" />
                </button>
                <span>{min}</span>
                <button
                  aria-label="Increase minimum number of options"
                  disabled={min === variants.length || min === max}
                  onClick={() => setMin(min + 1)}
                  type="button"
                  data-testid="min-plus"
                >
                  <i className="mm-plus" />
                </button>
              </div>
            </div>
          ) : null}
          <div className={styles.TopOptions}>
            <p>Maximum number of options customers can select</p>
            <div className={styles.Incrementer}>
              <button
                aria-label="Decrease maximum number of options"
                disabled={max === 0 || max === min}
                onClick={() => setMax(max - 1)}
                type="button"
                data-testid="max-minus"
              >
                <i className="mm-minus" />
              </button>
              <span>{max}</span>
              <button
                aria-label="Increase maximum number of options"
                disabled={max === variants.length}
                onClick={() => setMax(min > max ? min : max + 1)}
                type="button"
                data-testid="max-plus"
              >
                <i className="mm-plus" />
              </button>
            </div>
          </div>
          {optionParagraph && <p data-testid="optionGroup-paragraph">{optionParagraph}</p>}
        </>
      )}
      {isRequiredOption && (
        <p data-testid="optionGroup-paragraph">Customers must select an option as this is a required Option Group.</p>
      )}
      {variants && (
        <OptionList
          options={options}
          columnHeadings={headings}
          handleChange={handlePriceChange}
          handleNameChange={handleNameChange}
          handleToggle={handleToggle}
          dndId={optionGroup.posId}
          parentId={optionGroup.posId}
          isDragDisabled={isDragDisabled}
          toggleDragOnEdits={toggleDragOnEdits}
          currentVenue={currentVenue}
        />
      )}
      <div className={detailsStyles.PosID}>POS ID {posId}</div>
    </div>
  );
};

export default OptionGroupItem;
